import React, { useEffect, useImperativeHandle, useState } from 'react';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import {
  Box,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import ClientSelect from 'components/form/ClientSelect';
import FinancialYearMonthSelect from 'components/form/FinancialYearMonthSelect';
import KatakanaTextField from 'components/form/KatakanaTextField';
import TextArea from 'components/form/TextArea';
import UserSelect from 'components/form/UserSelect';
import TechStackSelect from 'components/tech-stack-select/TechStackSelect';
import {
  MSG_INVALID_DATETIME_FORMAT,
  MSG_MAX_LENGTH,
  MSG_REQUIRED,
} from 'constant/ValidationMessages';
import { UserSession } from 'stores/AuthenticationStoreAdapter';
import { ClientResult, listClient } from 'stores/ClientStoreAdapter';
import {
  MasterTypeResult,
  getDistribution,
  getIndustry,
  getProjectScale,
} from 'stores/MasterTypeStoreAdapter';
import {
  ManagerResult,
  TechStackResult,
  createProject,
  getTechStack,
  listManager,
} from 'stores/ProjectStoreAdapter';
import { errorDefault, successDefault } from 'utils/Toastify';

export type ProjectCreateFormHandle = {
  submit: () => void;
};

type ProjectCreateFormProps = {
  userSession: UserSession;
  onCreate: () => void;
  onCancel: () => void;
  setProgress: (v: boolean) => void;
  /** コンソールドロワー内で使う場合など、フォーム内蔵ヘッダーを非表示にする（既定: false） */
  hideHeader?: boolean;
};

/** yyyy-MM-dd'T'HH:mm:ss 形式の正規表現 */
const DATETIME_REGEX = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/;

/** DetailPanel の p:3 (24px) を打ち消す sticky ヘッダー sx */
const STICKY_HEADER_SX = {
  position: 'sticky',
  top: -24,
  zIndex: 2,
  bgcolor: 'background.paper',
  borderBottom: 1,
  borderColor: 'divider',
  pb: 1,
  mx: -3,
  px: 3,
  pt: 3,
  mt: -3,
} as const;

const ProjectCreateForm = React.forwardRef<ProjectCreateFormHandle, ProjectCreateFormProps>(
  ({ userSession, onCreate, onCancel, setProgress, hideHeader = false }, ref) => {
    const [projectName, setProjectName] = useState('');
    const [projectNameKana, setProjectNameKana] = useState('');
    const [managerId, setManagerId] = useState('');
    const [clientId, setClientId] = useState('');
    const [projectScaleId, setProjectScaleId] = useState('');
    const [distributionId, setDistributionId] = useState('');
    const [industryIds, setIndustryIds] = useState<string[]>([]);
    const [techStackIds, setTechStackIds] = useState<string[]>([]);
    const [startDate, setStartDate] = useState('');
    const [remarks, setRemarks] = useState('');

    const [managers, setManagers] = useState<ManagerResult[]>([]);
    const [clients, setClients] = useState<ClientResult[]>([]);
    const [projectScales, setProjectScales] = useState<MasterTypeResult[]>([]);
    const [distributions, setDistributions] = useState<MasterTypeResult[]>([]);
    const [industries, setIndustries] = useState<MasterTypeResult[]>([]);
    const [techStackData, setTechStackData] = useState<TechStackResult | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});

    useEffect(() => {
      Promise.all([
        listManager(),
        listClient({ includeExpired: false }),
        getProjectScale(),
        getDistribution(),
        getIndustry(),
        getTechStack(),
      ])
        .then(([managerList, clientList, scaleList, distList, indList, techData]) => {
          setManagers(managerList);
          setClients(clientList);
          setProjectScales(scaleList);
          setDistributions(distList);
          setIndustries(indList);
          setTechStackData(techData);
          // 課長一覧に自分が含まれていれば初期値として設定
          const selfManager = managerList.find((m) => m.userId === userSession.userId);
          if (selfManager) setManagerId(selfManager.userId);
          // ラジオボタン初期値: 最初の選択肢
          if (scaleList.length > 0) setProjectScaleId(scaleList[0].typeCode);
          if (distList.length > 0) setDistributionId(distList[0].typeCode);
        })
        .catch(() => errorDefault('マスタデータの取得に失敗しました'));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const validate = (): boolean => {
      const newErrors: Record<string, string> = {};

      if (!projectName) newErrors.projectName = MSG_REQUIRED('プロジェクト名');
      else if (projectName.length > 300)
        newErrors.projectName = MSG_MAX_LENGTH('プロジェクト名', 300);

      if (!projectNameKana) newErrors.projectNameKana = MSG_REQUIRED('プロジェクト名（カナ）');
      else if (projectNameKana.length > 300)
        newErrors.projectNameKana = MSG_MAX_LENGTH('プロジェクト名（カナ）', 300);

      if (!managerId) newErrors.managerId = MSG_REQUIRED('課長');
      if (!clientId) newErrors.clientId = MSG_REQUIRED('取引先');

      if (startDate && !DATETIME_REGEX.test(startDate))
        newErrors.startDate = MSG_INVALID_DATETIME_FORMAT('開始日');

      if (remarks && remarks.length > 10000) newErrors.remarks = MSG_MAX_LENGTH('備考', 10000);

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    };

    const handleCreate = () => {
      if (!validate()) return;

      setProgress(true);
      createProject({
        projectName,
        projectNameKana,
        managerId,
        clientId,
        ...(projectScaleId && { projectScaleId }),
        ...(distributionId && { distributionId }),
        ...(industryIds.length > 0 && { industryIds }),
        ...(techStackIds.length > 0 && { techStackIds }),
        ...(startDate && { startDate }),
        ...(remarks && { remarks }),
      })
        .then(() => {
          successDefault('プロジェクトを作成しました');
          onCreate();
        })
        .catch((e: Error) => {
          errorDefault('プロジェクトの作成に失敗しました', e.message);
        })
        .finally(() => setProgress(false));
    };

    useImperativeHandle(ref, () => ({ submit: handleCreate }));

    return (
      <Stack sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {!hideHeader && (
          <Box sx={STICKY_HEADER_SX}>
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Stack direction="row" alignItems="center" spacing={1}>
                <IconButton size="small" onClick={onCancel}>
                  <ArrowBackIcon fontSize="small" />
                </IconButton>
                <Typography variant="h6" fontWeight={700}>
                  プロジェクト登録
                </Typography>
              </Stack>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ ml: 4.5 }}>
              新しいプロジェクトを登録します
            </Typography>
          </Box>
        )}

        <Stack spacing={3} sx={{ flex: 1, overflow: 'auto', pt: hideHeader ? 0 : 2 }}>
          {/* 基本情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              基本情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <TextField
                  label="プロジェクト名"
                  value={projectName}
                  onChange={(e) => setProjectName(e.target.value)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.projectName}
                  helperText={errors.projectName}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              <Grid size={6}>
                <KatakanaTextField
                  label="プロジェクト名（カナ）"
                  value={projectNameKana}
                  onChange={(v) => setProjectNameKana(v)}
                  fullWidth
                  size="small"
                  required
                  error={!!errors.projectNameKana}
                  helperText={errors.projectNameKana}
                  slotProps={{ htmlInput: { maxLength: 300 } }}
                />
              </Grid>
              <Grid size={6}>
                <UserSelect
                  mode="single"
                  users={managers as unknown as import('stores/UserStoreAdapter').UserResult[]}
                  selectedUserId={managerId || null}
                  onChange={(id) => setManagerId(id || '')}
                  label="課長"
                  required
                  error={!!errors.managerId}
                  helperText={errors.managerId}
                />
              </Grid>
              <Grid size={6}>
                <ClientSelect
                  mode="single"
                  clients={clients}
                  selectedClientId={clientId || null}
                  onChange={(id) => setClientId(id || '')}
                  label="取引先"
                  required
                  error={!!errors.clientId}
                  helperText={errors.clientId}
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 補足情報 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              補足情報
            </Typography>
            <Grid container spacing={2}>
              <Grid size={12}>
                <FormControl>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>規模</FormLabel>
                  <RadioGroup
                    row
                    value={projectScaleId}
                    onChange={(e) => setProjectScaleId(e.target.value)}
                  >
                    {projectScales.map((s) => (
                      <FormControlLabel
                        key={s.typeCode}
                        value={s.typeCode}
                        control={<Radio size="small" />}
                        label={s.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl>
                  <FormLabel sx={{ fontSize: '0.875rem', mb: 0.5 }}>商流</FormLabel>
                  <RadioGroup
                    row
                    value={distributionId}
                    onChange={(e) => setDistributionId(e.target.value)}
                  >
                    {distributions.map((d) => (
                      <FormControlLabel
                        key={d.typeCode}
                        value={d.typeCode}
                        control={<Radio size="small" />}
                        label={d.typeLabel}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Grid>
              <Grid size={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>業界</InputLabel>
                  <Select
                    multiple
                    value={industryIds}
                    label="業界"
                    onChange={(e) => {
                      const value = e.target.value;
                      setIndustryIds(typeof value === 'string' ? value.split(',') : value);
                    }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 350 } } }}
                    renderValue={(selected) => {
                      const items = selected
                        .map((id) => {
                          const item = industries.find((i) => i.typeCode === id);
                          return item ? { id, name: item.typeLabel } : null;
                        })
                        .filter((item): item is { id: string; name: string } => !!item);
                      return (
                        <Stack direction="row" spacing={0.5} flexWrap="wrap">
                          {items.map(({ id, name }) => (
                            <Chip
                              key={id}
                              label={name}
                              size="small"
                              onDelete={(e: React.SyntheticEvent) => {
                                e.stopPropagation();
                                setIndustryIds(industryIds.filter((sid) => sid !== id));
                              }}
                              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
                            />
                          ))}
                        </Stack>
                      );
                    }}
                    sx={{ borderRadius: 2 }}
                  >
                    {industries.map((option) => (
                      <MenuItem key={option.typeCode} value={option.typeCode}>
                        <Checkbox
                          icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                          checkedIcon={<CheckBoxIcon fontSize="small" />}
                          checked={industryIds.includes(option.typeCode)}
                          size="small"
                          sx={{ mr: 1 }}
                        />
                        {option.typeLabel}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 技術スタック */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              技術スタック
            </Typography>
            <TechStackSelect
              techStackData={techStackData}
              selectedIds={techStackIds}
              onChange={setTechStackIds}
            />
          </Box>

          <Divider />

          {/* 期間 */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight={600}
              color="text.secondary"
              sx={{ mb: 1.5 }}
            >
              期間
            </Typography>
            <Grid container spacing={2}>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="start"
                  value={startDate}
                  onChange={setStartDate}
                  label="開始日"
                  error={!!errors.startDate}
                  helperText={errors.startDate}
                />
              </Grid>
              <Grid size={6}>
                <FinancialYearMonthSelect
                  dateType="end"
                  value=""
                  onChange={() => {}}
                  label="終了日"
                  disabled
                />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          {/* 備考 */}
          <TextArea
            label="備考"
            value={remarks}
            onChange={setRemarks}
            maxLength={10000}
            rows={6}
            fullWidth
            size="small"
            errorMessage={errors.remarks}
          />
        </Stack>
      </Stack>
    );
  },
);

ProjectCreateForm.displayName = 'ProjectCreateForm';

export default ProjectCreateForm;
